import { useState, useEffect, useMemo } from "react";
import { ApplicationRemovalTaskListItem } from "../../../../application/domain/application/application-removal-task/application-removal-task-list";
import apiClient from "../../../../application/apis/api-client";
import tenantManagementApiClient from "../../../../application/apis/tenant-management/tenant-management-client";
import { toast } from "react-toastify";
import {
  DefaultNumItemsPerPages,
  toTotalPage,
} from "../../../../application/domain/pagination/paging";
import { Auth } from "../../../../application/domain/auth/auth";
import * as Sentry from "@sentry/browser";

export const useApplicationRemovalTaskList = (
  auth: Auth,
  defaultCurrentPage?: number,
) => {
  const [ApplicationRemovalTasks, setApplicationRemovalTasks] = useState<
    ApplicationRemovalTaskListItem[]
  >([]);
  const [enableGroupIDFilter, setEnableGroupIDFilter] = useState<boolean>(
    !auth.hasRole("Admin"),
  );
  const [groupNameMap, setGroupNameMap] = useState<Record<string, string>>({});
  const [currentPage, setCurrentPage] = useState<number>(
    defaultCurrentPage || 1,
  );
  const [totalPage, setTotalPage] = useState<number>(0);

  const groupIds = useMemo(() => {
    // タスクのグループIDを重複なく取得する
    return Array.from(
      new Set(ApplicationRemovalTasks.map((task) => task.groupId)),
    );
  }, [ApplicationRemovalTasks]);

  useEffect(() => {
    (async () => {
      const { data: groups } =
        await tenantManagementApiClient.fetchGroupsMetadata(
          1,
          groupIds.length,
          {
            ids: groupIds,
          },
        );
      const groupNameMap: Record<string, string> = {};
      groups.forEach((g) => {
        if (g.id) {
          groupNameMap[g.id] = g.name;
        }
      });
      setGroupNameMap(groupNameMap);
    })();
  }, [groupIds]);

  useEffect(() => {
    (async () => {
      // 申請削除タスク一覧の参照権限がない場合は、データを取得しない
      if (
        !auth.checkGroupPermissionWithoutTarget([
          "applications.removal_tasks_read",
        ])
      ) {
        return;
      }

      try {
        const { data: tasks, headers } =
          await apiClient.listRemovalApplicationTasks(
            currentPage,
            DefaultNumItemsPerPages,
            [],
            enableGroupIDFilter && !auth.hasRole("Admin") ? auth.groupIDs : [],
          );
        setApplicationRemovalTasks(tasks);
        setTotalPage(
          toTotalPage(
            Number(headers["pagination-count"]),
            DefaultNumItemsPerPages,
          ),
        );
      } catch (e) {
        toast.error("申請削除タスクの取得に失敗しました");
      }
    })();
  }, [auth, currentPage, enableGroupIDFilter]);

  const toggleGroupIDFilter = () => {
    setEnableGroupIDFilter((prev) => !prev);
  };

  const resolveGroupName = (groupId: string): string => {
    const groupName = groupNameMap[groupId];
    if (!groupName) {
      Sentry.captureMessage(
        `申請削除タスク一覧にて、グループ名の取得に失敗しました。: ${groupId}`,
        {
          level: "error",
        },
      );
    }
    return groupName || "不明なグループ";
  };

  return {
    ApplicationRemovalTasks,
    resolveGroupName,
    enableGroupIDFilter,
    toggleGroupIDFilter,
    currentPage,
    setCurrentPage,
    totalPage,
  };
};
